import jsStringEscape from "js-string-escape";
import search from "./queries/search.sparql.js";
import getClass from "./queries/get-class.sparql.js";
import getSurroundingsAttributes from "./queries/get-surroundings-attributes.sparql.js";
import getSurroundingsInwardsRelations from "./queries/get-surroundings-inwards-relations.sparql.js";
import getSurroundingsOutwardsRelations from "./queries/get-surroundings-outwards-relations.sparql.js";
import getSurroundingsParent from "./queries/get-surroundings-parent.sparql.js";
import getSurroundingsComplexAttributes from "./queries/get-surroundings-complex-attributes.sparql.js";
import getHierarchy from "./queries/get-hierarchy.sparql.js";
import getGroup from "./queries/get-group.sparql.js";
import { SparqlQueryRdfSource } from "@dataspecer/core/io/rdf/sparql/sparql-query-rdf-source";
import { RdfSourceWrap } from "@dataspecer/core/core/adapter/rdf";
import { isSgovClass, loadSgovClass, } from "./entity-adapters/sgov-class-adapter.js";
import { ReadOnlyMemoryStore } from "@dataspecer/core/core";
import { FederatedSource } from "@dataspecer/core/io/rdf/federated/federated-rdf-source";
import { RDFS, SKOS } from "./sgov-vocabulary.js";
import { isSgovAssociation, loadSgovAssociation, } from "./entity-adapters/sgov-association-adapter.js";
import { isSgovAttribute, loadSgovAttribute, } from "./entity-adapters/sgov-attribute-adapter.js";
const getSurroundings = [
    getSurroundingsAttributes,
    getSurroundingsInwardsRelations,
    getSurroundingsOutwardsRelations,
    getSurroundingsParent,
    getSurroundingsComplexAttributes,
];
const searchQuery = (searchString) => search({ query: `"${jsStringEscape(searchString)}"` });
const getClassQuery = (cimIri) => getClass({ node: `<${cimIri}>` });
const getHierarchyQuery = (cimIri) => getHierarchy({ node: `<${cimIri}>` });
const getGroupQuery = (cimIri) => getGroup({ node: `<${cimIri}>` });
const getSurroundingsQueries = (cimIri) => getSurroundings.map(q => q({ node: `<${cimIri}>` }));
const IRI_REGEXP = new RegExp("^https?://");
/**
 * Adapter for SGOV (Semantic Government Vocabulary) CIM type.
 *  - https://slovník.gov.cz/sparql
 */
export class SgovAdapter {
    endpoint;
    httpFetch;
    iriProvider;
    resourceGroupCache = new Map();
    constructor(endpoint, httpFetch) {
        this.endpoint = endpoint;
        this.httpFetch = httpFetch;
    }
    setIriProvider(iriProvider) {
        this.iriProvider = iriProvider;
    }
    async search(searchString) {
        if (!this.iriProvider) {
            throw new Error("Missing IRI provider.");
        }
        const source = new SparqlQueryRdfSource(this.httpFetch, this.endpoint, searchQuery(searchString));
        await source.query();
        const results = await source.property("__search_results", "__has_search_result");
        let sorted = [];
        for (const result of results) {
            const resultWrap = RdfSourceWrap.forIri(result.value, source);
            await this.cacheResourceGroup(resultWrap);
            sorted.push({
                sort: Number((await resultWrap.property("__order"))[0].value),
                cls: await loadSgovClass(resultWrap, this.iriProvider),
            });
        }
        sorted = sorted.sort((a, b) => a.sort - b.sort).map((p) => p.cls);
        if (IRI_REGEXP.test(searchString)) {
            const classByIri = await this.getClass(searchString);
            if (classByIri) {
                sorted = [classByIri, ...sorted];
            }
        }
        return sorted;
    }
    async getClass(cimIri) {
        if (!this.iriProvider) {
            throw new Error("Missing IRI provider.");
        }
        const source = new SparqlQueryRdfSource(this.httpFetch, this.endpoint, getClassQuery(cimIri));
        await source.query();
        const entity = RdfSourceWrap.forIri(cimIri, source);
        if (!(await isSgovClass(entity))) {
            return null;
        }
        await this.cacheResourceGroup(RdfSourceWrap.forIri(cimIri, source));
        return await loadSgovClass(entity, this.iriProvider);
    }
    async getSurroundings(cimIri) {
        if (!this.iriProvider) {
            throw new Error("Missing IRI provider.");
        }
        const sources = getSurroundingsQueries(cimIri).map((query) => new SparqlQueryRdfSource(this.httpFetch, this.endpoint, query));
        await Promise.all(sources.map((q) => q.query()));
        const source = FederatedSource.createExhaustive(sources);
        const resources = await this.loadPimEntitiesGraphFromEntity(cimIri, source);
        return ReadOnlyMemoryStore.create(resources);
    }
    /**
     * Returns full known hierarchy structure - containing all ancestors and
     * descendants of the given class.
     * @param cimIri class CIM IRI
     */
    async getFullHierarchy(cimIri) {
        if (!this.iriProvider) {
            throw new Error("Missing IRI provider.");
        }
        const source = new SparqlQueryRdfSource(this.httpFetch, this.endpoint, getHierarchyQuery(cimIri));
        await source.query();
        const resources = await this.loadPimEntitiesGraphFromEntity(cimIri, source);
        return ReadOnlyMemoryStore.create(resources);
    }
    /**
     * Returns a glossary info for the resource.
     * @param cimIri
     */
    async getResourceGroup(cimIri) {
        if (!this.iriProvider) {
            throw new Error("Missing IRI provider.");
        }
        if (this.resourceGroupCache.has(cimIri)) {
            return this.resourceGroupCache.get(cimIri);
        }
        else {
            const source = new SparqlQueryRdfSource(this.httpFetch, this.endpoint, getGroupQuery(cimIri));
            await source.query();
            return await this.cacheResourceGroup(RdfSourceWrap.forIri(cimIri, source));
        }
    }
    /**
     * Takes an RdfSource and CIM iri of a class. From the class it loads
     * everything accessible (associations, attributes and parents) and
     * returns all the entities.
     */
    async loadPimEntitiesGraphFromEntity(rootClassCimIri, source) {
        const resources = {};
        const classesProcessed = new Set();
        const associationsProcessed = new Set();
        // Reverse lookup for classes that are descendants of the given class.
        const descendants = [rootClassCimIri];
        let processedDescendant = 0;
        while (processedDescendant < descendants.length) {
            const descendant = descendants[processedDescendant];
            const res = await source.reverseProperty(RDFS.subClassOf, descendant);
            for (const r of res) {
                if (!descendants.includes(r.value)) {
                    descendants.push(r.value);
                }
            }
            processedDescendant++;
        }
        // List of CIM iris to process
        let processQueue = [rootClassCimIri, ...descendants];
        while (processQueue.length) {
            const processedCimIri = processQueue.pop();
            if (classesProcessed.has(processedCimIri)) {
                continue;
            }
            classesProcessed.add(processedCimIri);
            // Class itself
            const rdfClassWrap = RdfSourceWrap.forIri(processedCimIri, source);
            const pimClass = await loadSgovClass(rdfClassWrap, this.iriProvider);
            resources[pimClass.iri] = pimClass;
            // Some classes may be empty because of simplification of SPARQL queries.
            // Therefore, we need to check, if it is actually a class and containing
            // a group information.
            if (await isSgovClass(rdfClassWrap)) {
                await this.cacheResourceGroup(rdfClassWrap);
            }
            // Process associations for the class and add class from the other side
            // of the association to `processQueue`
            const associations = [
                ...(await rdfClassWrap.reverseNodes(RDFS.domain)),
                ...(await rdfClassWrap.reverseNodes(RDFS.range)),
            ];
            for (const cimAssociationIri of associations) {
                const entity = RdfSourceWrap.forIri(cimAssociationIri, source);
                if (associationsProcessed.has(cimAssociationIri) ||
                    !(await isSgovAssociation(entity))) {
                    continue;
                }
                associationsProcessed.add(cimAssociationIri);
                const associationResources = await loadSgovAssociation(entity, source, this.iriProvider);
                associationResources.forEach((r) => (resources[r.iri] = r));
                // Add linked classes to the processQueue
                processQueue.push(await entity.node(RDFS.domain));
                processQueue.push(await entity.node(RDFS.range));
            }
            // Process attributes for the current class
            const attributes = await rdfClassWrap.reverseNodes(RDFS.domain);
            for (const cimAttributeIri of attributes) {
                const entity = RdfSourceWrap.forIri(cimAttributeIri, source);
                if (!(await isSgovAttribute(entity))) {
                    continue;
                }
                const pimAttribute = await loadSgovAttribute(entity, this.iriProvider);
                resources[pimAttribute.iri] = pimAttribute;
            }
            // Process class hierarchy
            processQueue = [
                ...processQueue,
                ...pimClass.pimExtends.map(this.iriProvider.pimToCim),
            ];
        }
        return resources;
    }
    async cacheResourceGroup(entity) {
        const groups = await entity.nodes(SKOS.inScheme);
        this.resourceGroupCache.set(entity.iri, groups);
        return groups;
    }
}
//# sourceMappingURL=sgov-adapter.js.map